import {hasher} from 'hasher';
import {getHash, setRouteHash} from '../constants/RouteDefinitions';
import {getRouteDefFromHash, getRouteMatchFromHash} from '../util/getRouteDefFromHash';
import {joinHashPathAndQuery, splitHashPathAndQuery} from '../util/hashQuery';
import {nsAmplify} from '../util/nsAmplify';
import {global} from '../appContext';
import {_d} from '../util/misc';

/** Strip leading `#/` so hasher APIs receive a path-only hash. */
export function stripRouteHash(hash: string): string {
    const regEx = /^(?:#\/)?(.*)$/;
    return hash.match(regEx)?.[1] ?? hash;
}

/**
 * Working org from the URL: `o/{orgnr}/…` first, else matched route `{orgnr}`
 * (e.g. `formview/{orgnr}/…`). Skips formlogin so pre-auth company memory stays separate.
 */
export function getOrgnrFromHash(hash: string | null | undefined): string | null {
    if (!hash) {
        return null;
    }
    const path = splitHashPathAndQuery(stripRouteHash(hash).replace(/^#/, '')).path;
    const prefixMatch = /^o\/([^/]+)\//i.exec(path);
    if (prefixMatch?.[1]) {
        return prefixMatch[1];
    }
    const matched = getRouteMatchFromHash(hash);
    if (!matched || matched.routeDef.id === 'formlogin') {
        return null;
    }
    const orgnr = matched.params['orgnr'];
    if (orgnr == null || String(orgnr) === '') {
        return null;
    }
    return String(orgnr);
}

/** Apply URL orgnr to the working org (no URL rewrite; no snackbar — boot/restore). */
export function syncWorkingOrgFromHash(hash: string | null | undefined): void {
    const orgnr = getOrgnrFromHash(hash);
    if (orgnr) {
        global.login.syncSelectedOrganizationFromRoute(orgnr, {announce: false});
    }
}

/** True when the hash path is “nowhere” (bare `#` / `#/`). */
export function isEmptyRoutePath(pathHash: string): boolean {
    return pathHash === '' || pathHash === '/';
}

/** Auth-chrome first path segments that are never a user destination to restore after login. */
const AUTH_CHROME_HASH_SEGMENTS = new Set(['login', 'logout', 'formlogin']);

/**
 * Reduce a raw location hash to a restorable deep link, or null when it is not one.
 * Empty/nowhere hashes and auth-chrome hashes (login/logout/formlogin) are dropped so the
 * post-login redirect never targets the sign-in flow itself.
 */
export function sanitizeIntendedHash(hash: string | null | undefined): string | null {
    if (!hash) {
        return null;
    }
    // Tolerate a bare '#' (some browsers) as well as '', '#/' and '#/…'.
    const path = stripRouteHash(hash).replace(/^#/, '');
    if (isEmptyRoutePath(path)) {
        return null;
    }
    const firstSegment = path.split('/')[0]?.toLowerCase() ?? '';
    return AUTH_CHROME_HASH_SEGMENTS.has(firstSegment) ? null : hash;
}

/**
 * Remember where the user was heading so login can restore it. The first real deep link wins:
 * an already-captured destination is never overwritten by a later auth-chrome hash (formlogin/SSO).
 */
export function captureIntendedHash(hash: string | null | undefined): void {
    if (global.login.intendedHash) {
        return;
    }
    const sanitized = sanitizeIntendedHash(hash);
    if (!sanitized) {
        return;
    }
    global.login.intendedHash = sanitized;
    _d.log(`Captured intended hash: "${sanitized}"`);
}

/**
 * Post-login / empty-hash home: lastHash, then role home (userstart or resetownpassword).
 */
export function navigateToAppHome(): void {
    const lastHash = nsAmplify.store('lastHash');
    if (lastHash) {
        _d.log(`Redirecting to last hash: "${lastHash}"`);
        hasher.replaceHash(stripRouteHash(lastHash));
        // replaceHash may no-op when unchanged — still apply org from that hash.
        syncWorkingOrgFromHash(lastHash);
        return;
    }

    const hasPermission = global.permissionValidator.isMinSuperEmployee();
    if (hasPermission) {
        _d.log('Redirecting to user start');
        const hash = getHash('userstart', {}, false);
        if (hash) {
            hasher.replaceHash(hash);
            syncWorkingOrgFromHash(hash);
        }
        return;
    }

    _d.log('Redirecting to change password');
    setRouteHash('resetownpassword');
}

/**
 * Full after-login cascade: redirectUrl → intendedHash (canSeeAfterLogin) → {@link navigateToAppHome}.
 */
export function navigateAfterLoginSuccess(options: {
    redirectUrl?: string | undefined;
    intendedHash?: string | null;
    onIntendedHashConsumed?: () => void;
}): void {
    const {redirectUrl, intendedHash, onIntendedHashConsumed} = options;

    if (redirectUrl) {
        const url = new URL(redirectUrl);
        if (url.origin == window.location.origin) {
            const canSeeAfterLogin = getRouteDefFromHash(url.hash)?.canSeeAfterLogin;
            if (canSeeAfterLogin !== false) {
                _d.log(`Redirecting to redirect url hash: "${url.hash}"`);
                window.location.hash = stripRouteHash(url.hash);
                syncWorkingOrgFromHash(url.hash);
                return;
            }
        } else {
            _d.log(`Redirecting to redirect url: "${url}"`);
            window.open(url, '_self');
            return;
        }
    }

    if (intendedHash) {
        const canSeeAfterLogin = getRouteDefFromHash(intendedHash)?.canSeeAfterLogin;
        if (canSeeAfterLogin !== false) {
            _d.log(`Redirecting to intended hash: "${intendedHash}"`);
            // Must write the hash: parseHash alone activates the route but leaves location.hash stale
            // (e.g. still on formlogin, or empty after a bare app open).
            hasher.replaceHash(stripRouteHash(intendedHash));
            // Same-hash reload often skips hasher.changed — sync org without relying on parseHash.
            syncWorkingOrgFromHash(intendedHash);
            onIntendedHashConsumed?.();
            return;
        }
    }

    navigateToAppHome();
}

/**
 * Empty/nowhere hash: session → app home; else → formlogin.
 * Always replaceHash so transitional empties do not stack in history.
 */
export function adjudicateEmptyRouteHash(hashSearch = ''): void {
    if (global.login.isLoggedIn()) {
        navigateToAppHome();
        return;
    }
    replaceWithFormLogin(hashSearch);
}

/**
 * True when no concrete route matches (http400 catch-all does not count).
 * {@link getRouteDefFromHash} already skips RegExp-only catch-alls.
 */
export function isUnmatchedRoutePath(pathHash: string): boolean {
    return !getRouteDefFromHash(pathHash);
}

/**
 * Invalid / unmatched hash: anonymous → formlogin; in session → leave for http400.
 * @returns true when navigation was handled (caller should stop parsing).
 */
export function adjudicateUnmatchedRouteHash(pathHash: string, hashSearch = ''): boolean {
    if (!isUnmatchedRoutePath(pathHash)) {
        return false;
    }
    if (global.login.isLoggedIn()) {
        return false;
    }
    _d.log(`Unmatched hash while anonymous; replacing with formlogin: "${pathHash}"`);
    replaceWithFormLogin(hashSearch);
    return true;
}

function replaceWithFormLogin(hashSearch = ''): void {
    const formLogin = getHash('formlogin', {}, false) ?? 'formlogin';
    hasher.replaceHash(joinHashPathAndQuery(formLogin, hashSearch));
}
